import { Component, ReactNode, useEffect } from "react"
import { connect, useDispatch, useSelector } from "react-redux"
import {
  Navigate,
  Outlet,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useNavigationType,
} from "react-router"

import client from "client/services/actions"
import {
  Admin,
  Call,
  Finale,
  Home,
  InsertRelative,
  Loading,
  Lobby,
  Login,
  NirAndExemption,
  NotAuthorized,
  NotFound,
  Payment,
  Profile,
  Signup,
  Statistics,
  Survey,
  Upload,
  VerifyPhone,
} from "containers"
import DermatoscopeVideo from "containers/DermatoscopeVideo"
import PharmacistInfo from "containers/PharmacistInfo/PharmacistInfo"
import TeleexpertiseRecap from "containers/TeleexpertiseRecap"
import SurveyPatch from "containers/SurveyPatch"
import TeleexpertiseConsent from "containers/TeleexpertiseConsent"
import TeleexpertiseEnd from "containers/TeleexpertiseEnd"
import Consent from "containers/Consent/Consent"
import Doctors from "containers/Doctors/Doctors"
import NoNetwork from "containers/Errors/NoNetwork/NoNetwork"
import CompletePatientInfo from "containers/CompletePatientInfo/CompletePatientInfo"
import { ImportedDocuments } from "containers/Profile/ImportedDocuments"
import ModifyRelative from "containers/Profile/ModifyRelative"
import ResetPassword from "containers/ResetPassword/ResetPassword"
import { Tutorial } from "containers/Tutorial/Index"
import { CallState } from "types/payload"
import { TutoState } from "types/props"
import { Dispatcher } from "types/redux"
import { ClientStore, ConfigurationStore, GlobalStore } from "types/store"

import DocumentNotFound from "containers/Errors/DocumentNotFound/DocumentNotFound"

import {
  ADMIN_PAGE,
  ADMIN_CARERS_PAGE,
  CALL_PAGE,
  CONSENT_PAGE,
  FINALE_PAGE,
  GET_DOCTOR_PAGE,
  HOME_PAGE,
  INSERT_RELATIVE_PAGE,
  LOBBY_PAGE,
  LOGIN_PAGE,
  MODIFY_RELATIVE_PAGE,
  NIR_AND_EXEMPTION_PAGE,
  NO_NETWORK_PAGE,
  NOT_AUTHORIZED_PAGE,
  PAYMENT_PAGE,
  PROFILE_PAGE,
  PROFILE_DOCUMENTS_PAGE,
  REGISTER_PAGE,
  RESET_PASSWORD_PAGE,
  SET_RELATIVE_PAGE,
  COMPLETE_PATIENT_INFO_PAGE,
  STATISTICS_PAGE,
  SURVEY_PAGE,
  TUTORIALS_PAGE,
  UPLOAD_DOC_PAGE,
  VERIFY_PHONE_PAGE,
  DOCTOR_CHOICE_PAGE,
  VITALS_PAGE,
  MAINTENANCE_PAGE,
  DIAGNOSTIC_DEVICES_PAGE,
  DOCUMENT_NOT_FOUND_PAGE,
  PREVENTION_REPORT_INFO,
  PHARMACIST_END_PAGE,
  DERMATOSCOPE_VIDEO_PAGE,
  DERMATO_RECAP_PAGE,
  PATCH_PAGE,
  DERMATO_PHARMACIST_INFO_PAGE,
  TELEEXPERTISE_CONSENT_PAGE,
  TELEEXPERTISE_END_PAGE,
  READERS_CONFIGURATION_PAGE,
  CHECKUP_REPORT_PAGE,
  CHECKUP_END_PAGE,
  SEARCH_DOCTOR_PAGE,
} from "./constants"
import { AdminCarer } from "containers/Carer/Carer"
import { DoctorChoice } from "containers/Doctors/DoctorChoice/DoctorChoice"
import { Vitals } from "containers/Vitals/Vitals"
import Maintenance from "containers/Maintenance/Maintenance"
import DevicesDiagnostic from "containers/Diagnosis/Devices/Devices"
import PreventionTutorial from "containers/PreventionTutorial/PreventionTutorial"
import ReadersConfiguration from "containers/ContactlessPayment/ReadersConfiguration"
import CheckupReport from "containers/Checkup/CheckupReport"
import CheckupEnd from "containers/Checkup/CheckupEnd"
import PharmacistEnd from "containers/PreventionEnd/PharmacistEnd"
import paymentActions from "containers/Payment/services/actions"
import actionsSurvey from "containers/Survey/services/actions"
import { DoctorsSearch } from "containers/DoctorSearch/DoctorSearch"

interface ProtectedRouteProps {
  isAllowed: boolean
  redirectPath: string
  children?: ReactNode
}

const ProtectedRoute = ({
  isAllowed,
  redirectPath,
  children,
}: ProtectedRouteProps) => {
  if (!isAllowed) {
    return <Navigate to={redirectPath} replace />
  }
  return children ? <>{children}</> : <Outlet />
}

const TeleexpertiseSentGuard = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const navigationType = useNavigationType()
  const { pathname } = useLocation()
  const sent = useSelector(
    ({ payment }: GlobalStore) => !!payment?.teleexpertiseSent,
  )
  const allowed =
    pathname === TELEEXPERTISE_END_PAGE ||
    pathname === PROFILE_PAGE ||
    (pathname === TELEEXPERTISE_CONSENT_PAGE && navigationType !== "POP")
  const pending = sent && !allowed

  useEffect(() => {
    if (!pending) return
    if (navigationType === "POP") {
      // The reset that normally runs with the navigation to the confirmation
      // may not have happened yet when the back comes this early.
      dispatch(actionsSurvey.reset())
      navigate(PROFILE_PAGE, { replace: true })
    } else {
      // A new parcours starts on purpose: disarm the guard. The survey and the
      // upload stores are already reset by the flow itself.
      dispatch(paymentActions.reset())
    }
  }, [pending, navigationType, navigate, dispatch])

  if (pending) return null
  return <Outlet />
}

interface RootProps {
  fetchInstallation: () => void
  success: boolean
  admin: boolean
  login: boolean
  loading: boolean
  verified: boolean
  call: boolean
  maintenance?: boolean
}

class Root extends Component<RootProps> {
  state = { shouldRefresh: false }

  constructor(props: RootProps) {
    super(props)
    this.props.fetchInstallation()
  }

  /**
   *
   * TODO
   * replace <Route path="*" element={<Home />} />
   * by  <Route path="*" element={<NotFound />} />
   * after migration from pharma to kiosk
   */
  public render = (): ReactNode => {
    if (
      this.props.maintenance ||
      /^\/maintenance/.test(window.location.pathname)
    ) {
      return <Maintenance />
    }
    if (window.location.pathname === NO_NETWORK_PAGE) return <NoNetwork />

    if (this.props.loading) return <Loading />

    if (!this.props.success) {
      return <NotAuthorized />
    }

    const { admin, login, verified, call } = this.props

    return (
      <Routes>
        <Route path={MAINTENANCE_PAGE} element={<Maintenance />} />
        <Route path={NO_NETWORK_PAGE} element={<NoNetwork />} />
        <Route path={NOT_AUTHORIZED_PAGE} element={<NotAuthorized />} />
        <Route path={DOCUMENT_NOT_FOUND_PAGE} element={<DocumentNotFound />} />

        {/* An active call takes over every guarded route except the call page itself */}
        <Route
          element={<ProtectedRoute isAllowed={!call} redirectPath={CALL_PAGE} />}
        >
          {/* No pharmacy admin logged in */}
          <Route
            element={
              <ProtectedRoute isAllowed={!admin} redirectPath={HOME_PAGE} />
            }
          >
            <Route path="/" element={<Home />} />
            <Route path={ADMIN_PAGE} element={<Admin />} />
          </Route>

          {/* Pharmacy admin required */}
          <Route
            element={
              <ProtectedRoute isAllowed={admin} redirectPath={ADMIN_PAGE} />
            }
          >
            <Route path={HOME_PAGE} element={<Home />} />
            <Route path={ADMIN_CARERS_PAGE} element={<AdminCarer />} />
            <Route path={STATISTICS_PAGE} element={<Statistics />} />
            <Route path={NIR_AND_EXEMPTION_PAGE} element={<NirAndExemption />} />
            <Route path={READERS_CONFIGURATION_PAGE} element={<ReadersConfiguration />} />
            <Route
              path={TUTORIALS_PAGE}
              element={
                <Tutorial
                  onChoice={function (state: TutoState): void {
                    throw new Error("Function not implemented.")
                  }}
                />
              }
            />

            {/* No patient logged in */}
            <Route
              element={
                <ProtectedRoute isAllowed={!login} redirectPath={PROFILE_PAGE} />
              }
            >
              <Route path={REGISTER_PAGE} element={<Signup />} />
              <Route path={LOGIN_PAGE} element={<Login />} />
              <Route path={LOBBY_PAGE} element={<Lobby />} />
              <Route
                path={RESET_PASSWORD_PAGE}
                element={
                  <ResetPassword
                    history={{
                      push: function (s: string): void {
                        throw new Error("Function not implemented.")
                      },
                    }}
                  />
                }
              />
            </Route>

            {/* Patient logged in */}
            <Route
              element={
                <ProtectedRoute isAllowed={login} redirectPath={LOBBY_PAGE} />
              }
            >
              <Route path={GET_DOCTOR_PAGE} element={<Doctors />} />
              <Route path={DOCTOR_CHOICE_PAGE} element={<DoctorChoice />} />
              <Route
                path={`${MODIFY_RELATIVE_PAGE}/:id`}
                element={<ModifyRelative />}
              />
              <Route
                path={PREVENTION_REPORT_INFO}
                element={<PreventionTutorial />}
              />
              <Route path={CHECKUP_REPORT_PAGE} element={<CheckupReport />} />
              <Route path={SEARCH_DOCTOR_PAGE} element={<DoctorsSearch />} />

              {/* Patient verified */}
              <Route
                element={
                  <ProtectedRoute
                    isAllowed={verified}
                    redirectPath={VERIFY_PHONE_PAGE}
                  />
                }
              >
                <Route element={<TeleexpertiseSentGuard />}>
                  <Route path={SURVEY_PAGE} element={<Survey />} />
                  <Route path={VITALS_PAGE} element={<Vitals />} />
                  <Route path={FINALE_PAGE} element={<Finale />} />
                  <Route path={CHECKUP_END_PAGE} element={<CheckupEnd />} />
                  <Route path={PHARMACIST_END_PAGE} element={<PharmacistEnd />} />
                  <Route path={UPLOAD_DOC_PAGE} element={<Upload />} />
                  <Route path={CONSENT_PAGE} element={<Consent />} />
                  <Route path={PROFILE_PAGE} element={<Profile />} />
                  <Route path={PROFILE_DOCUMENTS_PAGE} element={<ImportedDocuments />} />
                  <Route
                    path={INSERT_RELATIVE_PAGE}
                    element={<InsertRelative />}
                  />
                  <Route
                    path={SET_RELATIVE_PAGE}
                    element={<CompletePatientInfo />}
                  />
                  <Route
                    path={COMPLETE_PATIENT_INFO_PAGE}
                    element={<CompletePatientInfo />}
                  />
                  <Route path={PAYMENT_PAGE} element={<Payment />} />
                  <Route
                    path={DERMATOSCOPE_VIDEO_PAGE}
                    element={<DermatoscopeVideo />}
                  />
                  <Route
                    path={DERMATO_PHARMACIST_INFO_PAGE}
                    element={<PharmacistInfo />}
                  />
                  <Route
                    path={DERMATO_RECAP_PAGE}
                    element={<TeleexpertiseRecap />}
                  />
                  <Route
                    path={`${PATCH_PAGE}/:questionId`}
                    element={<SurveyPatch />}
                  />
                  <Route
                    path={TELEEXPERTISE_CONSENT_PAGE}
                    element={<TeleexpertiseConsent />}
                  />
                  <Route
                    path={TELEEXPERTISE_END_PAGE}
                    element={<TeleexpertiseEnd />}
                  />
                </Route>
              </Route>

              {/* Patient not verified yet */}
              <Route
                element={
                  <ProtectedRoute
                    isAllowed={!verified}
                    redirectPath={PROFILE_PAGE}
                  />
                }
              >
                <Route path={VERIFY_PHONE_PAGE} element={<VerifyPhone />} />
              </Route>
            </Route>
          </Route>

          {/* Pharmacy admin or patient */}
          <Route
            element={
              <ProtectedRoute
                isAllowed={admin || login}
                redirectPath={LOBBY_PAGE}
              />
            }
          >
            <Route
              path={DIAGNOSTIC_DEVICES_PAGE}
              element={<DevicesDiagnostic />}
            />
          </Route>
        </Route>

        {/* Call page keeps the verified chain but must stay reachable during a call */}
        <Route
          element={
            <ProtectedRoute isAllowed={admin} redirectPath={ADMIN_PAGE} />
          }
        >
          <Route
            element={
              <ProtectedRoute isAllowed={login} redirectPath={LOBBY_PAGE} />
            }
          >
            <Route
              element={
                <ProtectedRoute
                  isAllowed={verified}
                  redirectPath={VERIFY_PHONE_PAGE}
                />
              }
            >
              <Route path={CALL_PAGE} element={<Call />} />
            </Route>
          </Route>
        </Route>

        <Route path="*" element={<NotFound />} />
      </Routes>
    )
  }
}

export const mapStateToProps = ({
  client,
  configuration,
}: {
  configuration: ConfigurationStore
  client: ClientStore
}): RootProps => {
  return {
    success: client.success,
    loading: client.loading,
    admin: !!client.admin,
    login: !!client.customer,
    call: !!client.call && client.call.state !== CallState.ENDED,
    verified: client.customer?.is_verified ? true : false,
    maintenance: configuration?.maintenance?.active,
  } as RootProps
}

const mapDispatchToProps = (dispatch: Dispatcher): Partial<RootProps> => {
  return {
    fetchInstallation: () => dispatch(client.installation),
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(Root as any)
